// pages/JoinWaitlist.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { addToWaitlist, patientHasAppointment } from "../../APIs/waitlistApi";
import { lookupAppointmentByStart } from "../../APIs/appointmentsApi";
import CompletePatientProfileModal from "../../Patient/CompletePatientProfileModal";
import { fetchCurrentUser } from "../../APIs/authApi";
import keycloak from "../../APIs/keycloak";

// helpers (local)
function fmtDateTime(zdtLike) {
  if (!zdtLike) return { date: "", time: "" };
  const iso = String(zdtLike).split("[")[0]; // support "…-04:00[America/New_York]"
  const d = new Date(iso);
  const date = d.toLocaleDateString([], {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return { date, time };
}

export default function JoinWaitlist({ patientId: patientIdProp }) {
  const navigate = useNavigate();

  // params & query
  const { patientId: patientIdParam, providerId } = useParams();
  const patientId = patientIdProp ?? patientIdParam;

  const { search } = useLocation();
  const qs = new URLSearchParams(search);
  const providerName = qs.get("providerName") || "";
  const apptStatus = qs.get("status") || "BOOKED";
  const appointmentId = qs.get("appointmentId"); // ✅ Get from query string
  const startRaw = qs.get("start"); // e.g. 2025-09-22T10:00:00-04:00[America/New_York]

  const start = useMemo(
    () => (startRaw ? decodeURIComponent(startRaw) : ""),
    [startRaw],
  );

  const { date, time } = useMemo(() => fmtDateTime(start), [start]);

  // zone for lookup fallback
  const zone = useMemo(() => {
    try {
      return (
        Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"
      );
    } catch {
      return "America/New_York";
    }
  }, []);

  // form state
  const [reasonForWaitlist, setReasonForWaitlist] = useState("");
  const [allowProviderContact, setAllowProviderContact] = useState(false);

  // UX state
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // 🔹 Gating state (BASIC_ACCOUNT vs COMPLETED_PROFILE)
  const [currentUser, setCurrentUser] = useState(null);
  const [showCompleteProfileModal, setShowCompleteProfileModal] =
    useState(false);
  const token = keycloak?.token;

  // ✅ NEW: Check if patient already booked this appointment
  const [alreadyBooked, setAlreadyBooked] = useState(false);
  const [checkingBooking, setCheckingBooking] = useState(true);

  useEffect(() => {
    if (!patientId || !providerId || !appointmentId) {
      setCheckingBooking(false);
      return;
    }

    (async () => {
      try {
        const exists = await patientHasAppointment({
          providerId: Number(providerId),
          appointmentId: Number(appointmentId),
          patientId: Number(patientId),
        });
        setAlreadyBooked(!!exists);
      } catch (e) {
        console.error("Failed to check if patient has appointment:", e);
        // Don’t block the page just because the check failed
        setAlreadyBooked(false);
      } finally {
        setCheckingBooking(false);
      }
    })();
  }, [patientId, providerId, appointmentId]);

  // 🔹 Load current user for accountPhase gating
  useEffect(() => {
    (async () => {
      try {
        const me = await fetchCurrentUser();
        setCurrentUser(me);
      } catch (e) {
        console.error("Failed to fetch current user for waitlist gating:", e);
        setCurrentUser(null);
      }
    })();
  }, []);

  // 🔹 Core join-waitlist logic
  const handleJoinWaitlistCore = async () => {
    setErr("");

    if (!patientId) {
      setErr("Missing patient id.");
      return;
    }
    if (!providerId) {
      setErr("Missing provider id.");
      return;
    }

    try {
      setSubmitting(true);

      // If appointmentId wasn't provided in the route, look it up by start time.
      let effectiveAppointmentId = appointmentId
        ? Number(appointmentId)
        : undefined;

      if (!effectiveAppointmentId) {
        if (!start) {
          throw new Error("Missing appointment start time for lookup.");
        }
        const lookup = await lookupAppointmentByStart(Number(providerId), {
          start,
          zone,
        });
        if (!lookup?.appointmentId) {
          throw new Error("Could not find appointment by start time.");
        }
        effectiveAppointmentId = Number(lookup.appointmentId);
      }

      await addToWaitlist(Number(patientId), {
        appointmentId: effectiveAppointmentId,
        providerId: Number(providerId),
        reasonForWaitlist: reasonForWaitlist?.trim() || undefined,
        allowProviderContact,
      });

      alert(
        "You're on the waitlist for this appointment. We’ll notify you if it frees up.",
      );
      navigate(-1);
    } catch (e) {
      setErr(
        e?.response?.data?.message ||
          e?.response?.data ||
          e?.message ||
          "Failed to join waitlist",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // 🔹 Gated submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      alert("Unable to determine your account status. Please sign in again.");
      return;
    }

    if (currentUser.accountPhase === "BASIC_ACCOUNT") {
      setShowCompleteProfileModal(true);
      return;
    }

    await handleJoinWaitlistCore();
  };

  return (
    <>
      {/* 🔵 Complete profile modal – auto-joins waitlist on completion */}
      <CompletePatientProfileModal
        isOpen={showCompleteProfileModal}
        onClose={() => setShowCompleteProfileModal(false)}
        token={token}
        currentUser={currentUser}
        onCompleted={async () => {
          setCurrentUser((prev) =>
            prev ? { ...prev, accountPhase: "COMPLETED_PROFILE" } : prev,
          );
          setShowCompleteProfileModal(false);

          // ✅ Wait a moment for backend to process
          await new Promise((resolve) => setTimeout(resolve, 500));

          // ✅ Refresh Keycloak token to get updated claims
          try {
            await keycloak.updateToken(30);
          } catch (err) {
            console.error("Failed to refresh token:", err);
          }
          await handleJoinWaitlistCore();
        }}
      />

      <div className="max-w-md mx-auto p-4 space-y-4">
        <h1 className="text-2xl font-bold">Join Waitlist</h1>

        {/* ✅ Show loading while checking */}
        {checkingBooking ? (
          <p className="text-gray-600">Checking appointment status...</p>
        ) : alreadyBooked ? (
          <div className="p-4 rounded bg-blue-50 border border-blue-200">
            <p className="text-blue-800 font-medium">
              You have already booked this appointment.
            </p>
            <p className="text-sm text-blue-600 mt-2">
              You cannot join the waitlist for an appointment you've already
              booked.
            </p>

            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-block mt-4 text-blue-600 hover:underline"
            >
              ← Go back
            </button>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-700">
              This appointment time is currently booked. Join the waitlist and
              we’ll notify you if it opens up.
            </p>

            {/* Appointment summary */}
            <div className="p-4 border rounded bg-white space-y-2">
              {providerName && (
                <div>
                  <div className="text-xs uppercase text-gray-500">
                    Provider
                  </div>
                  <div className="font-medium">{providerName}</div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs uppercase text-gray-500">Date</div>
                  <div className="font-medium">{date || "—"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500">Time</div>
                  <div className="font-medium">{time || "—"}</div>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase text-gray-500">Status</div>
                <span
                  className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                    apptStatus === "BOOKED"
                      ? "bg-blue-100 text-blue-700"
                      : apptStatus === "SCHEDULED"
                        ? "bg-indigo-100 text-indigo-700"
                        : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {apptStatus}
                </span>
              </div>

              {/* Technical IDs (optional to show) */}
              <div className="text-xs text-gray-500">
                Appt ID: {appointmentId || "—"} • Provider ID: {providerId} •
                Patient ID: {patientId}
              </div>
            </div>

            {/* Error (if any) */}
            {err && (
              <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200">
                {err}
              </div>
            )}

            {/* Waitlist form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Reason */}
              <div>
                <label className="block mb-1">Reason (optional)</label>
                <textarea
                  value={reasonForWaitlist}
                  onChange={(e) => setReasonForWaitlist(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="e.g., Prefer an earlier slot, urgent follow-up, etc."
                  rows={3}
                />
              </div>

              {/* Allow contact */}
              <div className="flex items-center gap-2">
                <input
                  id="allowProviderContact"
                  type="checkbox"
                  checked={allowProviderContact}
                  onChange={(e) => setAllowProviderContact(e.target.checked)}
                  className="h-4 w-4"
                />
                <label htmlFor="allowProviderContact" className="text-sm">
                  Allow the provider (or staff) to contact me about this
                  opening.
                </label>
              </div>

              {/* ✅ Submit button - disabled when submitting */}
              <button
                type="submit"
                disabled={submitting}
                className={`${
                  submitting
                    ? "bg-green-300"
                    : "bg-green-500 hover:bg-green-600"
                } text-white px-4 py-2 rounded`}
              >
                {submitting ? "Joining…" : "Join Waitlist"}
              </button>
            </form>

            <div className="text-sm">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="text-blue-600 hover:underline"
              >
                ← Back
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
